/**
 * Operator input controller for protocol v1 (PROTOCOL.md §3) — framework-free.
 *
 * - Keeps a registry of input sources: the keyboard (always), every connected gamepad
 *   (W3C Gamepad API), and the XR controllers while an immersive session runs.
 * - Exactly ONE source is active at a time (or none): input from every other device is
 *   ignored and never sent.
 * - Streams the active source's RAW data as `operator-input` frames — keyboard/gamepad
 *   on a fixed timer, XR per headset frame (pushed in by the host from teleop-xr, since
 *   page timers don't run in immersive mode). Frames go out even when nothing is
 *   pressed: they are the heartbeat the backend watchdog relies on.
 * - Selecting another source sends `send-stop` then `input-source-select`; losing the
 *   active device (gamepad unplugged, XR session ended, window blur for the keyboard)
 *   sends `send-stop` with the matching reason.
 *
 * Uses only platform APIs (navigator.getGamepads, window/document events) and the
 * injected `OperatorProtocol` — no React, no DOM elements, no robot state.
 */

import { createStore } from "zustand/vanilla";
import type { StoreApi } from "zustand/vanilla";

import type {
  GamepadData,
  InputData,
  InputKind,
  InputSourceRef,
  OperatorProtocol,
  StopReason,
  XrData,
} from "../teleop-commands";

export const KEYBOARD_SOURCE_ID = "keyboard";
export const XR_SOURCE_ID = "xr";
export const DEFAULT_INPUT_RATE_HZ = 25;
/** How often the live preview/stats in the store are refreshed (the wire rate is higher). */
const PREVIEW_INTERVAL_MS = 100;

export interface InputSourceInfo extends InputSourceRef {
  connected: boolean;
}

export interface OperatorInputState {
  sources: InputSourceInfo[];
  activeId: string | null;
  /** Transport gate — frames are only produced while the session is usable. */
  enabled: boolean;
  xr: { supported: boolean; active: boolean };
  /** Latest data of the active source (throttled) — for a live indicator in the UI. */
  preview: InputData | null;
  sentFrames: number;
  droppedFrames: number;
}

export interface OperatorInputControllerOptions {
  protocol: OperatorProtocol;
  rateHz?: number;
  onStatus?: (text: string) => void;
}

export interface OperatorInputController {
  readonly store: StoreApi<OperatorInputState>;
  start(): void;
  stop(): void;
  /** Make `id` the only active source (null = control off). Unknown ids are ignored. */
  select(id: string | null): void;
  setEnabled(enabled: boolean): void;
  /** Re-announce the selected source (no stop, no state change) — for when the backend
   * lost it, e.g. after a backend restart. No-op without an active source. */
  resync(): void;
  /** Operator "Stop": send-stop and switch control off. */
  stopRobot(): void;
  setXrSupported(supported: boolean): void;
  /** XR session state + controller models (from teleop-xr). */
  setXrActive(active: boolean, profiles?: string[]): void;
  /** One XR sample (from teleop-xr's per-frame loop). */
  pushXrSample(data: XrData): void;
}

const keyboardSource: InputSourceInfo = {
  id: KEYBOARD_SOURCE_ID,
  kind: "keyboard",
  label: "Клавиатура",
  connected: true,
};

export function gamepadSourceId(index: number): string {
  return `gamepad:${index}`;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export function sampleGamepad(pad: Gamepad): GamepadData {
  const buttons = Array.from(pad.buttons ?? []);
  const pressed: number[] = [];
  buttons.forEach((b, i) => {
    if (b.pressed) pressed.push(i);
  });
  return {
    mapping: pad.mapping ?? "",
    axes: Array.from(pad.axes ?? [], round3),
    buttons: buttons.map((b) => round3(b.value ?? 0)),
    pressed,
  };
}

/** <input> types that don't take typed text — keys pressed while one of them has focus
 * (e.g. right after clicking the source radio button) are operator input, not typing. */
const NON_TEXT_INPUT_TYPES = new Set([
  "radio",
  "checkbox",
  "button",
  "submit",
  "reset",
  "range",
  "color",
  "file",
  "image",
]);

/** True when a key event belongs to text entry (it must not drive the robot). */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== "object") return false;
  const el = target as { tagName?: string; isContentEditable?: boolean; type?: string };
  const tag = (el.tagName || "").toUpperCase();
  if (el.isContentEditable === true || tag === "TEXTAREA" || tag === "SELECT") return true;
  return tag === "INPUT" && !NON_TEXT_INPUT_TYPES.has(String(el.type || "text").toLowerCase());
}

/** Keys whose default browser action (scrolling, button activation) is suppressed while
 * the keyboard is the active source. */
const SUPPRESS_DEFAULT = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"]);

function sameSources(a: InputSourceInfo[], b: InputSourceInfo[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const o = b[i];
    return (
      o !== undefined &&
      s.id === o.id &&
      s.label === o.label &&
      s.connected === o.connected &&
      (s.profiles ?? []).join() === (o.profiles ?? []).join()
    );
  });
}

export function createOperatorInputController({
  protocol,
  rateHz = DEFAULT_INPUT_RATE_HZ,
  onStatus,
}: OperatorInputControllerOptions): OperatorInputController {
  const store = createStore<OperatorInputState>(() => ({
    sources: [keyboardSource],
    activeId: null,
    enabled: false,
    xr: { supported: false, active: false },
    preview: null,
    sentFrames: 0,
    droppedFrames: 0,
  }));

  const heldKeys = new Set<string>();
  let gamepadSources: InputSourceInfo[] = [];
  let xrProfiles: string[] = [];
  let timer = 0;
  let started = false;
  let lastPreviewAt = 0;
  let sent = 0;
  let dropped = 0;
  let gamepadLossReported = false;
  /** Date.now() of the last select/re-announce we sent — control-status packets
   * already in flight at that moment still show the old source, so a resync right
   * after it would only duplicate the select. */
  let lastSelectSentAt = -Infinity;
  const RESYNC_GRACE_MS = 1000;

  const get = () => store.getState();
  const activeSource = (): InputSourceInfo | null =>
    get().sources.find((s) => s.id === get().activeId) ?? null;

  function xrSource(): InputSourceInfo | null {
    const { supported, active } = get().xr;
    if (!supported && !active) return null;
    return {
      id: XR_SOURCE_ID,
      kind: "xr",
      label: xrProfiles.length ? `Meta Quest (${xrProfiles.join(", ")})` : "Meta Quest (VR)",
      profiles: xrProfiles.length ? [...xrProfiles] : undefined,
      connected: active,
    };
  }

  function publishSources() {
    const xr = xrSource();
    const next = [keyboardSource, ...gamepadSources, ...(xr ? [xr] : [])];
    if (!sameSources(next, get().sources)) store.setState({ sources: next });
  }

  function refreshGamepads() {
    const pads =
      typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : [];
    const next: InputSourceInfo[] = [];
    for (const pad of Array.from(pads)) {
      if (!pad || !pad.connected) continue;
      next.push({
        id: gamepadSourceId(pad.index),
        kind: "gamepad",
        label: pad.id,
        mapping: pad.mapping ?? "",
        connected: true,
      });
    }
    // Keep a selected-but-unplugged gamepad listed (disconnected) so the operator sees why
    // control stopped instead of the entry silently vanishing.
    const active = get().activeId;
    if (active?.startsWith("gamepad:") && !next.some((s) => s.id === active)) {
      const prev = gamepadSources.find((s) => s.id === active);
      if (prev) next.push({ ...prev, connected: false });
    }
    gamepadSources = next;
    publishSources();
  }

  function toRef(src: InputSourceInfo): InputSourceRef {
    const ref: InputSourceRef = { id: src.id, kind: src.kind, label: src.label };
    if (src.mapping !== undefined) ref.mapping = src.mapping;
    if (src.profiles) ref.profiles = src.profiles;
    return ref;
  }

  function emit(src: InputSourceInfo, data: InputData) {
    if (protocol.sendInput({ id: src.id, kind: src.kind }, data)) sent += 1;
    else dropped += 1;
    const now = Date.now();
    if (now - lastPreviewAt >= PREVIEW_INTERVAL_MS) {
      lastPreviewAt = now;
      store.setState({ preview: data, sentFrames: sent, droppedFrames: dropped });
    }
  }

  function tick() {
    refreshGamepads();
    const src = activeSource();
    if (!get().enabled || !src || src.kind === "xr") return;
    if (typeof document !== "undefined" && document.hidden) return;

    if (src.kind === "keyboard") {
      emit(src, { keys: [...heldKeys] });
      return;
    }
    const index = Number(src.id.slice("gamepad:".length));
    const pad = navigator.getGamepads?.()[index];
    if (!pad || !pad.connected) {
      if (!gamepadLossReported) {
        gamepadLossReported = true;
        protocol.stop("gamepad-disconnected");
        onStatus?.("Геймпад отключён — отправлен стоп");
      }
      return;
    }
    gamepadLossReported = false;
    emit(src, sampleGamepad(pad));
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (isEditableTarget(e.target)) return;
    heldKeys.add(e.code);
    if (get().activeId === KEYBOARD_SOURCE_ID && SUPPRESS_DEFAULT.has(e.code)) e.preventDefault();
  };

  const onKeyUp = (e: KeyboardEvent) => {
    heldKeys.delete(e.code);
  };

  const onBlur = () => {
    const hadKeys = heldKeys.size > 0;
    heldKeys.clear();
    if (hadKeys && get().activeId === KEYBOARD_SOURCE_ID && get().enabled) protocol.stop("blur");
  };

  const onPageHide = () => {
    if (get().activeId) protocol.stop("unload");
  };

  const onGamepadEvent = () => refreshGamepads();

  /** Switch the active source; `stopReason` is the send-stop that precedes the select
   * (PROTOCOL.md §3.4: stop → select → stream). */
  function setActive(id: string | null, stopReason: StopReason) {
    const current = get().activeId;
    if (id === current) return;
    const next = id === null ? null : (get().sources.find((s) => s.id === id) ?? null);
    if (id !== null && (!next || !next.connected)) return;
    if (current !== null) protocol.stop(stopReason);
    gamepadLossReported = false;
    store.setState({ activeId: next?.id ?? null, preview: null });
    lastSelectSentAt = Date.now();
    protocol.selectSource(next ? toRef(next) : null);
    onStatus?.(next ? `Источник управления: ${next.label}` : "Управление выключено");
  }

  return {
    store,

    start() {
      if (started) return;
      started = true;
      refreshGamepads();
      timer = window.setInterval(tick, 1000 / Math.max(1, rateHz));
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keyup", onKeyUp);
      window.addEventListener("blur", onBlur);
      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("gamepadconnected", onGamepadEvent);
      window.addEventListener("gamepaddisconnected", onGamepadEvent);
    },

    stop() {
      if (!started) return;
      started = false;
      clearInterval(timer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("gamepadconnected", onGamepadEvent);
      window.removeEventListener("gamepaddisconnected", onGamepadEvent);
      if (get().activeId) protocol.stop("unload");
      heldKeys.clear();
    },

    select: (id) => setActive(id, "source-change"),

    setEnabled(enabled) {
      if (enabled === get().enabled) return;
      store.setState({ enabled });
      // After a (re)connect the backend may have released control (owner timeout) —
      // re-announce the selected source so it accepts our frames again.
      const src = activeSource();
      if (enabled && src) protocol.selectSource(toRef(src));
    },

    resync() {
      const src = activeSource();
      if (!src || !get().enabled || Date.now() - lastSelectSentAt < RESYNC_GRACE_MS) return;
      lastSelectSentAt = Date.now();
      protocol.selectSource(toRef(src));
    },

    stopRobot() {
      if (get().activeId === null) protocol.stop("operator");
      else setActive(null, "operator");
    },

    setXrSupported(supported) {
      store.setState({ xr: { ...get().xr, supported } });
      publishSources();
    },

    setXrActive(active, profiles) {
      if (profiles) xrProfiles = profiles;
      const wasActive = get().xr.active;
      store.setState({ xr: { ...get().xr, active } });
      publishSources();
      if (wasActive && !active && get().activeId === XR_SOURCE_ID)
        setActive(null, "xr-session-end");
    },

    pushXrSample(data) {
      const src = activeSource();
      if (!get().enabled || src?.kind !== "xr") return;
      emit(src, data);
    },
  };
}

export type { InputKind };
